import {
  trackPageView, trackPhoneComparison, trackFavoriteToggle, trackPhoneView, trackSearch, trackFilterUsage, trackCurrencyChange, trackAuth, trackRecommendation, trackSaveComparison, trackTabChange, trackError } from './analytics';
import React, { useState, useMemo, useEffect } from 'react';
import { Search, X, Heart, Save, Star, DollarSign, Smartphone, Battery, Camera, Cpu, Monitor, LogOut, LogIn, Info, Award, Zap, TrendingUp, CheckCircle, XCircle, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

import {
  updatePhoneSEO,
  updateComparisonSEO,
  updateRankingsSEO,
  updateHomeSEO
} from './seo';


const API_URL = 'https://phonescope-backend.vercel.app/api';

const PhoneScope = () => {
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "" });
  const [authMode, setAuthMode] = useState("login");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [phones, setPhones] = useState([]);
  const [selectedPhones, setSelectedPhones] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ brand: 'all', priceRange: 'all', ram: 'all', network: 'all' });
  const [currency, setCurrency] = useState('US');
  const [activeTab, setActiveTab] = useState('home');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommendationForm, setRecommendationForm] = useState({ budget: 'medium', usage: 'general' });
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');


// NEW: Spec explanations from database
const [specExplanationsData, setSpecExplanationsData] = useState(null);


  // NEW: Phone details modal state
const [selectedPhoneDetails, setSelectedPhoneDetails] = useState(null);
const [showPhoneDetails, setShowPhoneDetails] = useState(false);

// NEW: Pagination and featured phones state
const [showFeatured, setShowFeatured] = useState(true);
const [currentPage, setCurrentPage] = useState(1);
const [phonesPerPage] = useState(9); // 9 phones per page (3x3 grid)

// ADD THIS FUNCTION HERE (after state, before other functions)
  const setActiveTabWithTracking = (tab) => {
    setActiveTab(tab);
    trackTabChange(tab);
    // Update SEO based on active tab
  if (tab === 'home') {
    updateHomeSEO();
  } else if (tab === 'rankings') {
    updateRankingsSEO(selectedCategory);
  } else if (tab === 'compare' && selectedPhones.length >= 2) {
    updateComparisonSEO(selectedPhones);
  }

  };

  const currencySymbols = { US: '$', UK: '£', EU: '€', India: '₹', Africa: 'R' };
  
// Get featured phones (top 6 phones based on average scores)
const featuredPhones = useMemo(() => {
  return [...phones]
    .map(phone => {
      const avgScore = Object.values(phone.score).reduce((a, b) => a + b, 0) / Object.values(phone.score).length;
      return { ...phone, avgScore };
    })
    .sort((a, b) => b.avgScore - a.avgScore)
    .slice(0, 6);
}, [phones]);


  const filteredPhones = useMemo(() => {
    return phones.filter(phone => {
      const matchesSearch = phone.name.toLowerCase().includes(searchTerm.toLowerCase()) || phone.brand.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBrand = filters.brand === 'all' || phone.brand.trim().toLowerCase() === filters.brand.toLowerCase();
      const matchesRam = filters.ram === 'all' || phone.specs.ram === filters.ram;
      const matchesNetwork = filters.network === 'all' || phone.specs.network === filters.network;
      let matchesPrice = true;
if (filters.priceRange !== 'all') {
  const price = phone.price[currency];
  
  // Price ranges adjusted per currency
  const priceRanges = {
    US: { budget: 500, mid: 900 },
    UK: { budget: 400, mid: 700 },
    EU: { budget: 450, mid: 800 },
    India: { budget: 40000, mid: 75000 },
    Africa: { budget: 8000, mid: 15000 }
  };
  
  const ranges = priceRanges[currency];
  
  if (filters.priceRange === 'budget') matchesPrice = price < ranges.budget;
  else if (filters.priceRange === 'mid') matchesPrice = price >= ranges.budget && price < ranges.mid;
  else if (filters.priceRange === 'premium') matchesPrice = price >= ranges.mid;
}
      return matchesSearch && matchesBrand && matchesRam && matchesNetwork && matchesPrice;
    });
  }, [searchTerm, filters, currency, phones]);

  useEffect(() => {
    fetchPhones();
    fetchSpecExplanations(); // NEW
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
      fetchUserData(token);
    }
   },   []);

    // Reset to page 1 when filters change
     useEffect(() => {
     setCurrentPage(1);
    }, [searchTerm, filters]);

    useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768);
  };
  
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

    // ✅ ADD THIS NEW useEffect HERE:
  useEffect(() => {
    if (searchTerm.length >= 3) {
      const timer = setTimeout(() => {
        trackSearch(searchTerm);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [searchTerm]);

   // ✅ ADD THIS NEW useEffect HERE:
  useEffect(() => {
    const hasActiveFilters = 
      filters.brand !== 'all' || 
      filters.priceRange !== 'all' || 
      filters.ram !== 'all' || 
      filters.network !== 'all';
    
    if (hasActiveFilters) {
      trackFilterUsage(filters);
    }
  }, [filters]);

  useEffect(() => {
  if (showPhoneDetails && selectedPhoneDetails) {
    updatePhoneSEO(selectedPhoneDetails, currency, currencySymbols[currency]);
  } else if (!showPhoneDetails && activeTab === 'home') {
    updateHomeSEO();
  }
}, [showPhoneDetails, selectedPhoneDetails, currency]);

// ============================================
// STEP 4: Update SEO when comparison changes
// ============================================
// Add this useEffect:

useEffect(() => {
  if (activeTab === 'compare' && selectedPhones.length >= 2) {
    updateComparisonSEO(selectedPhones);
  }
}, [selectedPhones, activeTab]);


// ============================================
// STEP 5: Update SEO when category changes in rankings
// ============================================
// Add this useEffect:

useEffect(() => {
  if (activeTab === 'rankings') {
    updateRankingsSEO(selectedCategory);
  }
}, [selectedCategory, activeTab]);


// ============================================
// STEP 6: Reset SEO on initial load
// ============================================
// Modify your first useEffect to include:

useEffect(() => {
  fetchPhones();
  fetchSpecExplanations();
  updateHomeSEO(); // Add this line
  
  const token = localStorage.getItem('token');
  const userData = localStorage.getItem('user');
  if (token && userData) {
    setUser(JSON.parse(userData));
    fetchUserData(token);
  }

}, []);

useEffect(() => {
  console.log('=== RESET TOKEN CHECK ===');
  console.log('Full URL:', window.location.href);
  console.log('Search params:', window.location.search);
  
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  
  console.log('Token extracted:', token);
  console.log('Token length:', token ? token.length : 0);
  
  if (token) {
    console.log('✅ Token found! Opening reset modal...');
    setResetToken(token);
    setShowResetPassword(true);
    setShowAuthModal(false);
  } else {
    console.log('❌ No token found in URL');
  }
}, []);

    

  const fetchPhones = async () => {
    try {
      const response = await fetch(`${API_URL}/phones`);
      const data = await response.json();
      setPhones(data);
    } catch (error) {
      console.error('Error fetching phones:', error);
    }
  };

// NEW: Fetch spec explanations
const fetchSpecExplanations = async () => {
  try {
    const response = await fetch(`${API_URL}/spec-explanations`);
    const data = await response.json();
    setSpecExplanationsData(data);
  } catch (error) {
    console.error('Error fetching spec explanations:', error);
  }
};


  const fetchUserData = async (token) => {
    try {
      const [favoritesRes, comparisonsRes] = await Promise.all([
        fetch(`${API_URL}/user/favorites`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/user/comparisons`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (favoritesRes.ok) {
        const favData = await favoritesRes.json();
        setFavorites(favData);
      }
      if (comparisonsRes.ok) {
        const compData = await comparisonsRes.json();
        setSavedComparisons(compData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleAuth = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const endpoint = authMode === 'login' ? 'login' : 'register';
    try {
      const response = await fetch(`${API_URL}/auth/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: authForm.name, email: authForm.email, password: authForm.password })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        setShowAuthModal(false);
        setAuthForm({ name: "", email: "", password: "" });
        fetchUserData(data.token);
        trackAuth(authMode); 
        alert(`${authMode === 'login' ? 'Login' : 'Registration'} successful!`);
      } else {
        alert(data.error || 'Authentication failed');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };
  
   const handleForgotPassword = async (e) => {
  e.preventDefault();
  try {
    const response = await fetch(`${API_URL}/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: forgotEmail })
    });

    const data = await response.json();
    
    if (response.ok) {
      setResetMessage('Password reset email sent! Check your inbox.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetMessage('');
        setForgotEmail('');
      }, 3000);
    } else {
      setResetMessage(data.error || 'Error sending reset email');
    }
  } catch (error) {
    setResetMessage('Error sending reset email. Please try again.');
  }
};



const handleResetPassword = async (e) => {
  e.preventDefault();
  
  if (newPassword.length < 6) {
    setResetMessage('Password must be at least 6 characters');
    return;
  }

  try {
    const response = await fetch(`${API_URL}/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resetToken, newPassword })
    });

    const data = await response.json();
    
    if (response.ok) {
      setResetMessage('Password reset successful! You can now login.');
      setTimeout(() => {
        setShowResetPassword(false);
        setShowAuthModal(true);
        setResetMessage('');
        setNewPassword('');
        setResetToken('');
        window.history.replaceState({}, '', window.location.pathname);
      }, 2000);
    } else {
      setResetMessage(data.error || 'Error resetting password');
    }
  } catch (error) {
    setResetMessage('Error resetting password. Please try again.');
  }
};

 
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setFavorites([]);
    setSavedComparisons([]);
  };

  const toggleFavorite = async (phoneId) => {
  if (!user) {
    alert('Please login to save favorites');
    setShowAuthModal(true);
    return;
     }
       const token = localStorage.getItem('token');
       const isFavorite = favorites.some(fav => fav._id === phoneId);
       const phone = phones.find(p => p._id === phoneId);
  
  try {
    const response = await fetch(`${API_URL}/user/favorites/${phoneId}`, {
      method: isFavorite ? 'DELETE' : 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      fetchUserData(token);
      trackFavoriteToggle(phone.name, phone.brand, isFavorite ? 'remove' : 'add');
    }
  } catch (error) {
    console.error('Error toggling favorite:', error);
    trackError('favorite_toggle', error.message);
  }
};

  const saveComparison = async () => {
    if (!user) {
      alert('Please login to save comparisons');
      setShowAuthModal(true);
      return;
    }
    if (selectedPhones.length < 2) {
      alert('Please select at least 2 phones to save comparison');
      return;
    }
    const name = prompt('Enter a name for this comparison:');
    if (!name) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/user/comparisons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name, phoneIds: selectedPhones.map(p => p._id) })
      });
      if (response.ok) {
        alert('Comparison saved successfully!');
        fetchUserData(token);
        trackSaveComparison(name, selectedPhones.length); // Add this
      }
    } catch (error) {
      console.error('Error saving comparison:', error);
    }
  };

  const loadComparison = (comparison) => {
    setSelectedPhones(comparison.phones);
    setActiveTabWithTracking('compare');
  };

  const deleteComparison = async (comparisonId) => {
    if (!window.confirm('Delete this comparison?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/user/comparisons/${comparisonId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchUserData(token);
      }
    } catch (error) {
      console.error('Error deleting comparison:', error);
    }
  };

  const getRecommendations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/phones/recommend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...recommendationForm, currency })
      });
      const data = await response.json();
      setRecommendations(data);
      setShowRecommendations(true);
      trackRecommendation(recommendationForm.budget, recommendationForm.usage, data.length); // Add this
    } catch (error) {
      console.error('Error getting recommendations:', error);
      trackError('recommendations', error.message);
      alert('Error getting recommendations');
    } finally {
      setLoading(false);
    }
  };

  const uniqueBrands = [...new Set(phones.map(phone => phone.brand.trim()))].sort();
  
  // Pagination logic
const indexOfLastPhone = currentPage * phonesPerPage;
const indexOfFirstPhone = indexOfLastPhone - phonesPerPage;
const currentPhones = filteredPhones.slice(indexOfFirstPhone, indexOfLastPhone);
const totalPages = Math.ceil(filteredPhones.length / phonesPerPage);

const handlePageChange = (pageNumber) => {
  setCurrentPage(pageNumber);
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleExploreClick = () => {
  setShowFeatured(false);
  setCurrentPage(1);
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
  

  const togglePhoneSelection = (phone) => {
    if (selectedPhones.find(p => p._id === phone._id)) {
      setSelectedPhones(selectedPhones.filter(p => p._id !== phone._id));
    } else if (selectedPhones.length < 4) {
      setSelectedPhones([...selectedPhones, phone]);
    }
     // Track when 2+ phones are selected
  const newSelection = selectedPhones.find(p => p._id === phone._id) 
    ? selectedPhones.filter(p => p._id !== phone._id)
    : [...selectedPhones, phone];
    
  if (newSelection.length >= 2) {
    trackPhoneComparison(newSelection.map(p => p.name));
  }
};

  const ComparisonView = () => {
  if (selectedPhones.length === 0) {
    return (
      <div className="text-center py-16">
        <Smartphone className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No phones selected</h3>
        <p className="text-gray-500">Select 2-4 phones to compare</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          onClick={saveComparison}
          className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
        >
          <Save className="w-4 h-4" />
          Save Comparison
        </button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="p-4 text-left font-semibold">Feature</th>
              {selectedPhones.map(phone => (
                <th key={phone._id} className="p-4 text-center min-w-[200px]">
                  <img 
                    src={phone.image} 
                    alt={phone.name}
                    className="w-24 h-24 object-cover rounded-lg mx-auto mb-3"
                  />
                  <div className="font-bold">{phone.name}</div>
                  <div className="text-sm text-gray-500">{phone.brand}</div>
                  <div className="text-lg font-bold text-blue-600 mt-2">
                    {currencySymbols[currency]}{phone.price[currency].toLocaleString()}
                  </div>
                  <button
                      onClick={() => togglePhoneSelection(phone)}
                      className={`mt-2 ${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium`}
                   >
                       Remove
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(selectedPhones[0].specs).map(spec => {
              const values = selectedPhones.map(p => p.specs[spec]);
              const allSame = values.every(v => v === values[0]);
              
              return (
                <tr key={spec} className="border-b border-gray-100">
                  <td className="p-4 font-medium capitalize">{spec.replace(/([A-Z])/g, ' $1').trim()}</td>
                  {selectedPhones.map(phone => (
                    <td key={phone._id} className={`p-4 text-center ${!allSame && 'bg-yellow-50'}`}>
                      {phone.specs[spec]}
                    </td>
                  ))}
                </tr>
              );
            })}
            
            <tr className="border-b border-gray-100 bg-green-50">
              <td className="p-4 font-medium">Pros</td>
              {selectedPhones.map(phone => (
                <td key={phone._id} className="p-4">
                  <ul className="text-sm space-y-1">
                    {phone.pros.map((pro, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span>{pro}</span>
                      </li>
                    ))}
                  </ul>
                </td>
              ))}
            </tr>
            
            <tr className="border-b border-gray-100 bg-red-50">
              <td className="p-4 font-medium">Cons</td>
              {selectedPhones.map(phone => (
                <td key={phone._id} className="p-4">
                  <ul className="text-sm space-y-1">
                    {phone.cons.map((con, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-red-600 mt-0.5">✗</span>
                        <span>{con}</span>
                      </li>
                    ))}
                  </ul>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};


  // Helper function to simplify technical specs
// Enhanced helper function with multiple levels and brand awareness
const getSimplifiedSpec = (specName, specValue, phone) => {
  const val = specValue.toLowerCase();
  const numValue = parseInt(specValue);
  const brand = phone?.brand?.toLowerCase() || '';
  
  // Brand-specific optimizations
  const isApple = brand.includes('apple') || brand.includes('iphone');
  const isPremiumBrand = isApple || brand.includes('samsung') || brand.includes('google');
  const isBudgetBrand = brand.includes('xiaomi') || brand.includes('realme') || brand.includes('poco');
  
  const specs = {
    display: () => {
      const hasOLED = val.includes('amoled') || val.includes('oled');
      const has120Hz = val.includes('120hz') || val.includes('144hz') || val.includes('165hz');
      const has90Hz = val.includes('90hz');
      
      if (hasOLED && has120Hz) return 'Premium display with vibrant colors, deep blacks, and ultra-smooth scrolling';
      if (hasOLED) return 'Premium display with stunning colors and perfect contrast';
      if (has120Hz) return 'Super smooth display - great for gaming and scrolling';
      if (has90Hz) return 'Smooth display for everyday use';
      return 'Good quality display for daily tasks';
    },
    
    processor: () => {
  // Use database data if available, otherwise fallback to defaults
  const flagshipChips = specExplanationsData?.processor?.flagship || [
    '8 gen 3', '8 gen 4', 'a18', 'a17', 'a16', 
    'tensor g4', 'tensor g3', 'dimensity 9300', 'dimensity 9200'
  ];
  const upperMidChips = specExplanationsData?.processor?.upperMid || [
    '8 gen 2', '8 gen 1', 'a15', 'tensor g2',
    'dimensity 9000', 'dimensity 8300', '7+ gen 3'
  ];
  const midrangeChips = specExplanationsData?.processor?.midrange || [
    '7 gen', 'dimensity 8', 'dimensity 7', 'a14', 'a13'
  ];
  
  const flagshipText = specExplanationsData?.processor?.flagship_text || 'Top-tier performance - handles everything from gaming to multitasking effortlessly';
  const upperMidText = specExplanationsData?.processor?.upperMid_text || 'Excellent performance for demanding apps and heavy gaming';
  const midrangeText = specExplanationsData?.processor?.midrange_text || 'Solid performance for everyday tasks and casual gaming';
  const defaultText = specExplanationsData?.processor?.default || 'Capable performance for daily use and light multitasking';
  
  const isFlagship = flagshipChips.some(chip => val.includes(chip));
  const isUpperMid = upperMidChips.some(chip => val.includes(chip));
  const isMidrange = midrangeChips.some(chip => val.includes(chip));
  
  if (isFlagship) return flagshipText;
  if (isUpperMid) return upperMidText;
  if (isMidrange) return midrangeText;
  return defaultText;
},
      
      
    
    ram: () => {
      // Apple devices need less RAM due to optimization
      if (isApple) {
        if (numValue >= 8) return 'Excellent multitasking with Apple\'s optimized memory management';
        if (numValue >= 6) return 'Smooth multitasking for everyday apps';
        return 'Handles daily tasks efficiently';
      }
      
      // Android devices
      if (numValue >= 16) return 'Exceptional multitasking - perfect for power users and heavy multitasking';
      if (numValue >= 12) return 'Excellent multitasking - run many apps simultaneously without slowdown';
      if (numValue >= 8) return 'Good multitasking capability for everyday use';
      if (numValue >= 6) return 'Adequate for basic multitasking and daily apps';
      return 'Best suited for light usage with few apps open';
    },
    
    storage: () => {
      if (numValue >= 512) return 'Massive storage - never worry about space for photos, videos, and apps';
      if (numValue >= 256) return 'Plenty of storage for all your photos, videos, games, and apps';
      if (numValue >= 128) return 'Good storage space for regular users';
      if (numValue >= 64) return 'Basic storage - may need cloud backup for heavy photo/video users';
      return 'Limited storage - requires frequent management';
    },
    
    battery: () => {
      // Apple's efficiency means smaller batteries last longer
      if (isApple) {
        if (numValue >= 4000) return 'All-day battery life even with heavy use';
        if (numValue >= 3500) return 'Full day battery with normal to moderate use';
        if (numValue >= 3000) return 'Gets you through a typical day';
        return 'May need midday charging with heavy use';
      }
      
      // Android flagships (Samsung, Google) with good optimization
      if (isPremiumBrand) {
        if (numValue >= 5500) return 'Exceptional battery life - easily lasts 1.5 to 2 days';
        if (numValue >= 5000) return 'All-day battery with plenty to spare for heavy users';
        if (numValue >= 4500) return 'Full day battery life with moderate to heavy use';
        if (numValue >= 4000) return 'Gets you through a full day with normal use';
        return 'May need charging before end of day with heavy use';
      }
      
      // Budget brands (may have less optimization)
      if (numValue >= 6000) return 'Massive battery - easily 1.5 to 2 days between charges';
      if (numValue >= 5000) return 'All-day battery life with room to spare';
      if (numValue >= 4500) return 'Full day battery with normal use';
      if (numValue >= 4000) return 'Gets you through a typical day';
      return 'May need charging during the day with heavy use';
    },
    
    charging: () => {
      if (numValue >= 100) return 'Ultra-fast charging - 0 to 100% in under 30 minutes';
      if (numValue >= 65) return 'Super fast charging - full charge in about 30-40 minutes';
      if (numValue >= 45) return 'Very fast charging - 0 to 50% in around 20 minutes';
      if (numValue >= 30) return 'Fast charging - convenient top-ups throughout the day';
      if (numValue >= 20) return 'Standard fast charging - full charge in about an hour';
      return 'Basic charging speed - takes a few hours for full charge';
    },
    
    camera: () => {
      const mainMP = numValue;
      
      // Apple cameras (megapixels don't tell full story)
      if (isApple) {
        if (val.includes('48mp') || val.includes('50mp')) return 'Excellent camera system with industry-leading image processing';
        return 'High-quality photos with Apple\'s advanced computational photography';
      }
      
      // Premium brands with good sensors
      if (isPremiumBrand) {
        if (mainMP >= 200) return 'Flagship camera with exceptional detail and zoom capabilities';
        if (mainMP >= 100) return 'Professional-grade camera with outstanding quality in all conditions';
        if (mainMP >= 50) return 'Excellent camera for photos and videos in any lighting';
        if (mainMP >= 20) return 'Very good camera quality for everyday photography';
        return 'Capable camera for social media and casual photos';
      }
      
      // Budget brands (higher MP doesn't always mean better)
      if (mainMP >= 200) return 'High-resolution camera - great detail in good lighting';
      if (mainMP >= 100) return 'Very high megapixel count - excellent photos in good conditions';
      if (mainMP >= 50) return 'Good camera quality for the price point';
      if (mainMP >= 20) return 'Decent photos for social media and everyday use';
      return 'Basic camera suitable for casual snapshots';
    },
    
    os: () => {
      if (val.includes('ios')) {
        const version = parseInt(val.match(/\d+/)?.[0] || '0');
        if (version >= 17) return 'Latest iOS with newest features and 5+ years of updates';
        if (version >= 15) return 'Modern iOS with years of updates remaining';
        return 'iOS with Apple\'s seamless ecosystem integration';
      }
      
      if (val.includes('android')) {
        const version = parseInt(val.match(/\d+/)?.[0] || '0');
        if (version >= 14) return 'Latest Android with newest features and security';
        if (version >= 13) return 'Modern Android with regular updates';
        if (version >= 12) return 'Stable Android version with good app support';
        return 'Android system with wide app compatibility';
      }
      
      return 'Modern operating system with regular updates';
    },
    
    network: () => {
      if (val.includes('5g')) return '5G connectivity for ultra-fast downloads and future-proof networking';
      if (val.includes('4g') || val.includes('lte')) return '4G LTE - reliable fast internet for everyday use';
      return 'Standard mobile connectivity';
    }
  };
  
  // Return the appropriate explanation or fallback
  return specs[specName] ? specs[specName]() : 'Quality feature for this price range';
};

  const PhoneCard = ({ phone }) => {
    const isFavorite = favorites.some(fav => fav._id === phone._id);
    return (
      <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden">
        <div className="relative h-48 bg-gray-100 overflow-hidden">
          <img src={phone.image} alt={phone.name} className={`w-full h-full ${isMobile ? 'object-cover' : 'object-contain p-2'} hover:scale-105 transition-transform duration-300`} />
          <button onClick={() => toggleFavorite(phone._id)} className={`absolute top-2 right-2 p-2 rounded-full ${isFavorite ? 'bg-red-500 text-white' : 'bg-white text-gray-600'} hover:scale-110 transition-transform`}>
            <Heart className={`w-5 h-5 ${isFavorite && 'fill-current'}`} />
          </button>
        </div>
        <div className="p-6">
          <h3 className="text-xl font-bold mb-1">{phone.name}</h3>
          <p className="text-gray-500 mb-3">{phone.brand}</p>
          <div className="text-2xl font-bold text-blue-600 mb-4">{currencySymbols[currency]}{phone.price[currency].toLocaleString()}</div>
          <div className="space-y-2 mb-4 text-sm">
            <div className="flex items-center gap-2"><Monitor className="w-4 h-4 text-gray-400 flex-shrink-0" /><span className="truncate">{phone.specs.display}</span></div>
            <div className="flex items-center gap-2"><Cpu className="w-4 h-4 text-gray-400 flex-shrink-0" /><span className="truncate">{phone.specs.processor}</span></div>
            <div className="flex items-center gap-2"><Camera className="w-4 h-4 text-gray-400 flex-shrink-0" /><span className="truncate">{phone.specs.camera.split('+')[0]}</span></div>
            <div className="flex items-center gap-2"><Battery className="w-4 h-4 text-gray-400 flex-shrink-0" /><span className="truncate">{phone.specs.battery}</span></div>
          </div>
          <div className="flex gap-2">
          <button
            onClick={() => {
            setSelectedPhoneDetails(phone);
            setShowPhoneDetails(true);
            trackPhoneView(phone.name, phone.brand, phone.price[currency], currency);
            }}
            className="flex-1 py-2 px-4 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
  >
    View Details
           </button>
           <button
          onClick={() => togglePhoneSelection(phone)}
           className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            selectedPhones.find(p => p._id === phone._id)
        ? 'bg-red-500 text-white hover:bg-red-600'
           : 'bg-blue-500 text-white hover:bg-blue-600'
           }`}
           disabled={selectedPhones.length >= 4 && !selectedPhones.find(p => p._id === phone._id)}
  >
            {selectedPhones.find(p => p._id === phone._id) ? 'Remove' : 'Compare'}
         </button>
         </div>
        </div>
      </div>
    );
  };


  const FeaturedSection = () => (
  <div className="mb-8">
    <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg shadow-lg p-8 mb-6 text-white">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="w-8 h-8" />
        <h2 className="text-3xl font-bold">Featured Phones</h2>
      </div>
      <p className="text-blue-100">Handpicked top performers across all categories</p>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
      {featuredPhones.map(phone => (
        <PhoneCard key={phone._id} phone={phone} />
      ))}
    </div>
    
    <div className="text-center">
      <button
        onClick={handleExploreClick}
        className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
      >
        🔍 Explore All {phones.length} Phones
      </button>
    </div>
  </div>
);


const PaginationControls = () => {
  if (totalPages <= 1) return null;
  
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="flex justify-center items-center gap-2 mt-8">
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-4 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <ChevronLeft className="w-4 h-4" />
        Previous
      </button>
      
      {getPageNumbers().map((page, index) => (
        page === '...' ? (
          <span key={`ellipsis-${index}`} className="px-3 py-2">...</span>
        ) : (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`px-4 py-2 rounded-lg font-medium ${
              currentPage === page
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {page}
          </button>
        )
      ))}
      
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-4 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        Next
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};


  const RecommendationSection = () => (
    <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-lg p-8 mb-8 text-white">
      <h2 className="text-3xl font-bold mb-4">🎯 Find Your Perfect Phone</h2>
      <p className="mb-6 text-blue-100">Answer a few questions and get personalized recommendations</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block mb-2 font-semibold">What's your budget?</label>
          <select value={recommendationForm.budget} onChange={(e) => setRecommendationForm({ ...recommendationForm, budget: e.target.value })} className="w-full px-4 py-3 rounded-lg text-gray-800 focus:ring-2 focus:ring-white outline-none">
             <option value="low">Budget Friendly</option>
             <option value="medium">Mid-Range</option>
            <option value="high">Premium</option>
          </select>
        </div>
        <div>
          <label className="block mb-2 font-semibold">What will you use it for?</label>
          <select value={recommendationForm.usage} onChange={(e) => setRecommendationForm({ ...recommendationForm, usage: e.target.value })} className="w-full px-4 py-3 rounded-lg text-gray-800 focus:ring-2 focus:ring-white outline-none">
            <option value="gaming">Gaming & Performance</option>
            <option value="camera">Photography & Camera</option>
            <option value="social">Social Media & Content</option>
            <option value="general">General Everyday Use</option>
          </select>
        </div>
      </div>
      <button onClick={getRecommendations} disabled={loading} className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors disabled:opacity-50">
        {loading ? 'Finding...' : 'Get Recommendations'}
      </button>
      {showRecommendations && recommendations.length > 0 && (
        <div className="mt-8 space-y-4">
          <h3 className="text-2xl font-bold">📱 Top Recommendations for You</h3>
          {recommendations.map((phone, index) => (
  <div key={phone._id} className={`bg-white text-gray-800 rounded-lg p-4 flex ${isMobile ? 'flex-col items-center text-center' : 'flex-row items-center'} gap-4`}>
    <div className={`text-4xl font-bold text-blue-600 ${isMobile ? 'w-full' : 'w-12'} text-center flex-shrink-0`}>
      #{index + 1}
    </div>
    
    <img 
      src={phone.image} 
      alt={phone.name} 
      className={`w-24 h-24 ${isMobile ? 'object-cover mx-auto' : 'object-cover'} rounded-lg flex-shrink-0`}
    />
    
    <div className={isMobile ? 'w-full text-center' : 'flex-1'}>
          <h4 className={`text-xl font-bold mb-1 ${isMobile ? 'whitespace-normal' : ''}`}>{phone.name}</h4>
          <p className="text-gray-600 mb-2">{phone.brand} - {currencySymbols[currency]}{phone.price[currency].toLocaleString()}</p>
          <p className="text-sm text-gray-700">{phone.reason}</p>
    </div>
    
            <button 
            onClick={() => togglePhoneSelection(phone)} 
             className={`${
               selectedPhones.find(p => p._id === phone._id)
             ? 'bg-red-500 hover:bg-red-600'
             : 'bg-blue-500 hover:bg-blue-600'
              } text-white px-4 py-2 rounded-lg ${isMobile ? 'w-full' : 'flex-shrink-0 self-center'}`}
           >
                {selectedPhones.find(p => p._id === phone._id) ? 'Remove' : 'Compare'}
              </button>
             </div>
           ))}
        </div>
      )}
    </div>
  );

  const CategoryRankings = () => {
  const categories = {
    all: { name: 'All Phones', icon: Smartphone },
    camera: { name: 'Best Camera', icon: Camera },
    gaming: { name: 'Best Gaming', icon: Cpu },
    battery: { name: 'Best Battery', icon: Battery },
    design: { name: 'Best Design', icon: Star },
    value: { name: 'Best Value', icon: DollarSign }
  };
  
  const rankedPhones = [...phones].sort((a, b) => {
    if (selectedCategory === 'all') return b.price.US - a.price.US;
    return (b.score[selectedCategory] || 0) - (a.score[selectedCategory] || 0);
  }).filter(phone => selectedCategory === 'all' || phone.category.includes(selectedCategory));
  
  const SelectedIcon = categories[selectedCategory].icon;
  
  return (
    <div>
      {/* Desktop: Show buttons */}
      <div className="hidden md:flex gap-2 mb-6 flex-wrap">
        {Object.entries(categories).map(([key, cat]) => {
          const Icon = cat.icon;
          return (
            <button 
              key={key} 
              onClick={() => setSelectedCategory(key)} 
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap ${selectedCategory === key ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <Icon className="w-4 h-4" />{cat.name}
            </button>
          );
        })}
      </div>

      {/* Mobile: Show dropdown */}
      <div className="md:hidden mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Category</label>
        <div className="relative">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-white text-base font-medium"
          >
            {Object.entries(categories).map(([key, cat]) => (
              <option key={key} value={key}>
                {cat.name}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
            <SelectedIcon className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {rankedPhones.map((phone, index) => (
            <div key={phone._id} className={`bg-white rounded-lg shadow-md p-4 flex ${isMobile ? 'flex-col items-center text-center' : 'flex-row items-center'} gap-4`}>
  <div className={`text-3xl font-bold text-gray-300 ${isMobile ? 'w-full text-left' : 'w-16 text-left'} flex-shrink-0`}>
       #{index + 1}
  </div>
  
  <img 
    src={phone.image} 
    alt={phone.name} 
    className={`w-16 h-16 object-cover rounded-lg flex-shrink-0 ${isMobile ? 'mx-auto' : ''}`}
  />
  
  <div className={isMobile ? 'w-full text-center' : 'flex-1 min-w-0'}>
    <h3 className={`text-lg font-bold ${isMobile ? 'whitespace-normal' : 'truncate'}`}>
      {phone.name}
    </h3>
    <p className="text-gray-500 text-sm">{phone.brand}</p>
  </div>
  
  {selectedCategory !== 'all' && (
    <div className={isMobile ? 'w-full text-center' : 'text-right flex-shrink-0'}>
      <div className="text-2xl font-bold text-blue-600">{phone.score[selectedCategory]}</div>
      <div className="text-xs text-gray-500">Score</div>
    </div>
  )}
  
  <div className={isMobile ? 'w-full text-center' : 'text-right flex-shrink-0 min-w-[120px]'}>
    <div className="text-xl font-bold">{currencySymbols[currency]}{phone.price[currency].toLocaleString()}</div>
  </div>
  
  <button 
  onClick={() => togglePhoneSelection(phone)} 
  className={`${
    selectedPhones.find(p => p._id === phone._id)
      ? 'bg-red-500 hover:bg-red-600'
      : 'bg-blue-500 hover:bg-blue-600'
  } text-white px-4 py-2 rounded-lg ${isMobile ? 'w-full' : 'flex-shrink-0'}`}
>
  {selectedPhones.find(p => p._id === phone._id) ? 'Remove' : 'Compare'}
</button>
</div>
          ))}
        </div>
      </div>
    );
  };

const AuthModal = () => {
  const nameRef = React.useRef(null);
  const emailRef = React.useRef(null);
  const passwordRef = React.useRef(null);

  const handleSubmit = async () => {
    const formData = {
      name: nameRef.current?.value || '',
      email: emailRef.current?.value || '',
      password: passwordRef.current?.value || ''
    };

    const endpoint = authMode === 'login' ? 'login' : 'register';
    
    try {
      const response = await fetch(`${API_URL}/auth/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        setShowAuthModal(false);
        
        // Clear the refs
        if (nameRef.current) nameRef.current.value = '';
        if (emailRef.current) emailRef.current.value = '';
        if (passwordRef.current) passwordRef.current.value = '';
        
        fetchUserData(data.token);
        alert(`${authMode === 'login' ? 'Login' : 'Registration'} successful!`);
      } else {
        alert(data.error || 'Authentication failed');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {authMode === "login" ? "Login" : "Sign Up"}
          </h2>
          <button 
            onClick={() => setShowAuthModal(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {authMode === "register" && (
            <div>
              <label htmlFor="auth-name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                ref={nameRef}
                id="auth-name"
                name="name"
                type="text"
                placeholder="Enter your name"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                autoComplete="name"
              />
            </div>
          )}

          <div>
            <label htmlFor="auth-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              ref={emailRef}
              id="auth-email"
              name="email"
              type="email"
              placeholder="Enter your email"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="auth-password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              ref={passwordRef}
              id="auth-password"
              name="password"
              type="password"
              placeholder="Enter your password"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              autoComplete="current-password"
            />


            {/* Add this "Forgot Password" link */}
             <button
             type="button"
               onClick={() => {
              console.log('Forgot Password clicked!'); // Add this line
              setShowAuthModal(false);
              setShowForgotPassword(true);
              }}
                 className="text-sm text-blue-600 hover:text-blue-800 text-right block w-full mt-1"
              >
               Forgot Password?
              </button>
          </div>

          <button
            onClick={handleSubmit}
            className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600"
          >
            {authMode === "login" ? "Login" : "Sign Up"}
          </button>
        </div>

        <p className="text-center mt-4 text-gray-600">
          {authMode === "login" ? "Don't have an account? " : "Already have an account? "}
          <button
            onClick={() => setAuthMode(authMode === "login" ? "register" : "login")}
            className="text-blue-500 hover:underline font-semibold"
          >
            {authMode === "login" ? "Sign Up" : "Login"}
          </button>
        </p>
        {/* At the bottom of AuthModal, after the form */}
        <p className="text-xs text-gray-500 text-center mt-4">
      🔒 Password resets only sent from <strong>noreply@phonescopeglobal.com</strong>
        </p>
      </div>
    </div>
  );
};







const PhoneDetailsModal = ({ phone }) => {
  if (!phone) return null;

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 75) return 'text-blue-600 bg-blue-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-orange-600 bg-orange-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 75) return 'Very Good';
    if (score >= 60) return 'Good';
    return 'Average';
  };

  return (
    <div 
       className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
       onClick={() => setShowPhoneDetails(false)}
    >
    <div 
        className="bg-white rounded-lg max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
    >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
  <div className="flex justify-between items-start gap-4">
    <div className="flex-1">
      <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 mb-3`}>
        {phone.name}
      </h2>
      <div className="flex items-center gap-4 flex-wrap">
        <span className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold text-blue-600`}>
          {currencySymbols[currency]}{phone.price[currency].toLocaleString()}
        </span>
        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
          phone.specs.network === '5G' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
        }`}>
          {phone.specs.network}
        </span>
      </div>
    </div>
    <button 
      onClick={() => setShowPhoneDetails(false)}
      className="bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors shadow-lg flex-shrink-0"
      title="Close"
    >
      <X className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
    </button>
  </div>
</div>


        <div className="p-6">
          {/* Phone Image */}
          <div className="mb-8">
            <img 
                src={phone.image} 
                alt={phone.name}
                className={`w-full h-96 ${isMobile ? 'object-cover' : 'object-contain p-4'} rounded-lg shadow-lg`}
             />
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <button
              onClick={() => {
                toggleFavorite(phone._id);
              }}
              className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold ${
                favorites.some(fav => fav._id === phone._id)
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Heart className={`w-5 h-5 ${favorites.some(fav => fav._id === phone._id) && 'fill-current'}`} />
              {favorites.some(fav => fav._id === phone._id) ? 'Remove from Favorites' : 'Add to Favorites'}
            </button>
            <button
  onClick={() => {
    togglePhoneSelection(phone);
    setShowPhoneDetails(false);
  }}
  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold ${
    selectedPhones.find(p => p._id === phone._id)
      ? 'bg-red-500 text-white hover:bg-red-600'
      : 'bg-blue-500 text-white hover:bg-blue-600'
  }`}
>
  <TrendingUp className="w-5 h-5" />
  {selectedPhones.find(p => p._id === phone._id) ? 'Remove' : 'Compare'}
</button>
          </div>

          {/* Performance Scores */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Award className="w-6 h-6 text-blue-600" />
              Performance Ratings
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(phone.score).map(([category, score]) => (
                <div key={category} className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="flex justify-center mb-2">
                    {category === 'camera' && <Camera className="w-6 h-6 text-gray-600" />}
                    {category === 'gaming' && <Cpu className="w-6 h-6 text-gray-600" />}
                    {category === 'battery' && <Battery className="w-6 h-6 text-gray-600" />}
                    {category === 'design' && <Star className="w-6 h-6 text-gray-600" />}
                    {category === 'value' && <DollarSign className="w-6 h-6 text-gray-600" />}
                  </div>
                  <p className="text-sm text-gray-600 capitalize mb-2">{category}</p>
                  <div className={`text-3xl font-bold mb-1 ${getScoreColor(score).split(' ')[0]}`}>
                    {score}
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${getScoreColor(score)}`}>
                    {getScoreLabel(score)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Pros and Cons */}
          <div className="mb-8 grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                Strengths
              </h3>
              <ul className="space-y-2">
                {phone.pros.map((pro, i) => (
                  <li key={i} className="flex items-start gap-2 bg-green-50 p-3 rounded-lg">
                    <span className="text-green-600 mt-0.5">✓</span>
                    <span className="text-gray-700">{pro}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2 text-red-700">
                <XCircle className="w-5 h-5" />
                Considerations
              </h3>
              <ul className="space-y-2">
                {phone.cons.map((con, i) => (
                  <li key={i} className="flex items-start gap-2 bg-red-50 p-3 rounded-lg">
                    <span className="text-red-600 mt-0.5">✗</span>
                    <span className="text-gray-700">{con}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Technical Specifications */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Info className="w-6 h-6 text-blue-600" />
              Technical Specifications
            </h3>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid gap-4">
                {Object.entries(phone.specs).map(([key, value]) => (
                  <div key={key} className="border-b border-gray-200 pb-4 last:border-0 last:pb-0">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-gray-700 capitalize flex items-center gap-2">
                        {key === 'display' && <Monitor className="w-4 h-4" />}
                        {key === 'processor' && <Cpu className="w-4 h-4" />}
                        {key === 'camera' && <Camera className="w-4 h-4" />}
                        {key === 'battery' && <Battery className="w-4 h-4" />}
                        {key === 'charging' && <Zap className="w-4 h-4" />}
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className="text-gray-900 font-medium text-right">{value}</span>
                    </div>
                    <p className="text-sm text-gray-600 italic">
                      {getSimplifiedSpec(key, value, phone)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="mb-6">
            <h3 className="text-xl font-bold mb-3">Categories</h3>
            <div className="flex flex-wrap gap-2">
              {phone.category.map((cat) => (
                <span
                  key={cat}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold capitalize"
                >
                  {cat}
                </span>
              ))}
            </div>
          </div>

          {/* Buy on Amazon */}
          <div>
            <h3 className="text-xl font-bold mb-3">Buy on Amazon</h3>
            <p className="text-xs text-gray-500 mb-3 text-left">As an Amazon Associate, PhoneScope earns from qualifying purchases.</p>
           <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(phone.price).map(([region, price]) => {
                const amazonDomains = {
                  US: 'amazon.com',
                  UK: 'amazon.co.uk',
                  EU: 'amazon.de',
                  India: 'amazon.in',
                  Africa: 'amazon.com'  // South Africa uses US Amazon
                 };

             // Only add affiliate tag for US
            const amazonLink = region === 'US' 
         ? `https://www.${amazonDomains[region]}/s?k=${encodeURIComponent(phone.name)}&tag=phonescope-20`
         : `https://www.${amazonDomains[region]}/s?k=${encodeURIComponent(phone.name)}`;
                
                return (
                  
                  <a  key={region}
                    href={amazonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-b from-yellow-300 to-yellow-400 hover:from-yellow-400 hover:to-yellow-500 rounded-lg p-4 text-center transition-all transform hover:scale-105 shadow-md hover:shadow-lg cursor-pointer"
                  >
                    <p className="text-sm text-gray-800 font-semibold mb-1">
                      {region === 'US' && '🇺🇸 USA'}
                      {region === 'UK' && '🇬🇧 UK'}
                      {region === 'EU' && '🇪🇺 Europe'}
                      {region === 'India' && '🇮🇳 India'}
                      {region === 'Africa' && '🇿🇦 S. Africa'}
                    </p>
                    <p className="text-lg font-bold text-gray-900 mb-2">
                      {currencySymbols[region]}{price.toLocaleString()}
                    </p>
                    <p className="text-xs font-semibold text-gray-700">
                      Buy Now →
                    </p>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


  return (
    <div className="min-h-screen bg-gray-50">
      {showAuthModal && <AuthModal />}
      {showPhoneDetails && <PhoneDetailsModal phone={selectedPhoneDetails} />}
      {/* Forgot Password Modal */}
{showForgotPassword && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg p-8 max-w-md w-full">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Reset Password</h2>
        <button
          onClick={() => {
            setShowForgotPassword(false);
            setResetMessage('');
            setForgotEmail('');
          }}
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="w-6 h-6" />
        </button>
      </div>


      {/* WARNING #1 - Security Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
        <p className="text-xs text-yellow-800">
          ⚠️ <strong>Security Notice:</strong> Password reset emails will only be sent from <strong>noreply@phonescopeglobal.com</strong>. 
          If you receive a reset email from any other address, it is a scam.
        </p>
      </div>

      <form onSubmit={handleForgotPassword} className="space-y-4">
        <p className="text-gray-600 text-sm mb-4">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        <input
          type="email"
          placeholder="Email address"
          value={forgotEmail}
          onChange={(e) => setForgotEmail(e.target.value)}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />

        {resetMessage && (
          <div className={`p-3 rounded-lg text-sm ${
            resetMessage.includes('sent') || resetMessage.includes('successful')
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {resetMessage}
          </div>
        )}

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
        >
          Send Reset Link
        </button>

        <button
          type="button"
          onClick={() => {
            setShowForgotPassword(false);
            setShowAuthModal(true);
            setResetMessage('');
            setForgotEmail('');
          }}
          className="w-full text-gray-600 hover:text-gray-800 text-sm"
        >
          Back to Login
        </button>
      </form>
    </div>
  </div>
)}



{/* Reset Password Modal */}
      {showResetPassword && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
       <div className="bg-white rounded-lg p-8 max-w-md w-full animate-slideUp">
           <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Create New Password</h2>
          <button
          onClick={() => {
            setShowResetPassword(false);
            setResetMessage('');
            setNewPassword('');
            setResetToken('');
            window.history.replaceState({}, '', window.location.pathname);
          }}
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleResetPassword} className="space-y-4">
        <p className="text-gray-600 text-sm mb-4">
          Enter your new password below.
        </p>

        <input
          type="password"
          placeholder="New Password (min 6 characters)"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength="6"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />

        {resetMessage && (
          <div className={`p-3 rounded-lg text-sm ${
            resetMessage.includes('successful')
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {resetMessage}
          </div>
        )}

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
        >
          Reset Password
        </button>
      </form>
    </div>
  </div>
)}




      {!showPhoneDetails && !showAuthModal && !showForgotPassword && (
         <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className={`max-w-7xl mx-auto px-4 ${isMobile ? 'py-6' : 'py-4'}`}>
          <div className={`flex ${isMobile ? 'items-start' : 'items-center'} justify-between mb-4 gap-2 ${isMobile ? 'pt-1' : ''}`}>
  <h1 
       className={`${isMobile ? 'text-3xl' : 'text-3xl'} font-bold text-blue-600 cursor-pointer`}
       onClick={() => {
       setActiveTabWithTracking('home');
       setShowFeatured(true);
           }}
>
       📱 PhoneScope
</h1>
  <div className={`flex ${isMobile ? 'flex-col gap-2 pt-3' : 'items-center gap-4'}`}>
  <select 
    value={currency} 
    onChange={(e) => {setCurrency(e.target.value) ; trackCurrencyChange(e.target.value); }}
    className={`${isMobile ? 'px-3 py-2 text-sm w-full' : 'px-4 py-2'} border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none`}
  >
    <option value="US">🇺🇸 USD</option>
    <option value="UK">🇬🇧 GBP</option>
    <option value="EU">🇪🇺 EUR</option>
    <option value="India">🇮🇳 INR</option>
    <option value="Africa">🇿🇦 ZAR</option>
  </select>
  {user ? (
    <div className={`flex items-center ${isMobile ? 'gap-1 w-full' : 'gap-1'}`}>
        {!isMobile && <span className="text-gray-700">Hi, {user.name}!</span>}
        <button 
          onClick={handleLogout} 
          className={`flex items-center gap-1 ${isMobile ? 'justify-start px-3 py-3 text-sm w-full' : 'justify-center px-4 py-2'} bg-red-500 text-white rounded-lg hover:bg-red-600`}
        >
          <LogOut className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
           Logout
        </button>
      </div>
    ) : (
      <button 
         onClick={() => setShowAuthModal(true)} 
         className={`flex items-center gap-1 ${isMobile ? 'justify-start px-3 py-3 text-sm w-full' : 'justify-center px-4 py-2'} bg-blue-500 text-white rounded-lg hover:bg-blue-600`}
        >
       <LogIn className={`${isMobile ? 'w-4 h-4' : 'w-4 h-4'}`} />
        Login
      </button>
    )}
  </div>
</div>
         {/* Desktop nav */}
{!isMobile && (
  <nav className="flex gap-2 border-b border-gray-200 overflow-x-auto">
    <button onClick={() => setActiveTabWithTracking('home')} className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${activeTab === 'home' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-blue-600'}`}>Browse</button>
    <button onClick={() => setActiveTabWithTracking('compare')} className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${activeTab === 'compare' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-blue-600'}`}>Compare {selectedPhones.length > 0 && `(${selectedPhones.length})`}</button>
    <button onClick={() => setActiveTabWithTracking('rankings')} className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${activeTab === 'rankings' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-blue-600'}`}>Rankings</button>
    {user && (
      <>
        <button onClick={() => setActiveTabWithTracking('favorites')} className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${activeTab === 'favorites' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-blue-600'}`}>Favorites ({favorites.length})</button>
        <button onClick={() => setActiveTabWithTracking('saved')} className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${activeTab === 'saved' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-blue-600'}`}>Saved ({savedComparisons.length})</button>
      </>
    )}
  </nav>
)}

{/* Mobile hamburger menu */}
{isMobile && (
  <div className="border-b border-gray-200 -mt-14">
    <button 
      onClick={() => setShowMobileMenu(!showMobileMenu)}
      className="px-4 py-3 text-gray-700 font-medium"
    >
      <div className="flex flex-col gap-1">
        <span className="block w-6 h-0.5 bg-gray-700"></span>
        <span className="block w-6 h-0.5 bg-gray-700"></span>
        <span className="block w-6 h-0.5 bg-gray-700"></span>
      </div>
    </button>
    
    {showMobileMenu && (
      <div className="bg-white border-t border-gray-200">
        <button onClick={() => { setActiveTabWithTracking('home'); setShowMobileMenu(false); }} className={`w-full text-left px-4 py-3 font-medium ${activeTab === 'home' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'}`}>Browse</button>
        <button onClick={() => { setActiveTabWithTracking('compare'); setShowMobileMenu(false); }} className={`w-full text-left px-4 py-3 font-medium ${activeTab === 'compare' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'}`}>Compare {selectedPhones.length > 0 && `(${selectedPhones.length})`}</button>
        <button onClick={() => { setActiveTabWithTracking('rankings'); setShowMobileMenu(false); }} className={`w-full text-left px-4 py-3 font-medium ${activeTab === 'rankings' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'}`}>Rankings</button>
        {user && (
          <>
            <button onClick={() => { setActiveTabWithTracking('favorites'); setShowMobileMenu(false); }} className={`w-full text-left px-4 py-3 font-medium ${activeTab === 'favorites' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'}`}>Favorites ({favorites.length})</button>
            <button onClick={() => { setActiveTabWithTracking('saved'); setShowMobileMenu(false); }} className={`w-full text-left px-4 py-3 font-medium ${activeTab === 'saved' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'}`}>Saved ({savedComparisons.length})</button>
          </>
        )}
      </div>
    )}
  </div>
)}
        </div>
      </header>
      )}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'home' && (
  <>
    {showFeatured ? (
      <FeaturedSection />
    ) : (
      <>
        <RecommendationSection />
        
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">Browse All Phones</h3>
            <button
              onClick={() => setShowFeatured(true)}
              className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Back to Featured
            </button>
          </div>
          
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input type="text" placeholder="Search phones..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select value={filters.brand} onChange={(e) => setFilters({ ...filters, brand: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="all">All Brands</option>
              {uniqueBrands.map(brand => (<option key={brand} value={brand}>{brand}</option>))}
            </select>
            <select value={filters.priceRange} onChange={(e) => setFilters({ ...filters, priceRange: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="all">All Prices</option>
              <option value="budget">Budget</option>
              <option value="mid">Mid-range</option>
              <option value="premium">Premium</option>
            </select>
            <select value={filters.ram} onChange={(e) => setFilters({ ...filters, ram: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="all">All RAM</option>
              <option value="8GB">8GB</option>
              <option value="12GB">12GB</option>
              <option value="16GB">16GB</option>
            </select>
            <select value={filters.network} onChange={(e) => setFilters({ ...filters, network: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="all">All Networks</option>
              <option value="5G">5G</option>
              <option value="4G">4G</option>
            </select>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <p className="text-gray-600">
              Showing {indexOfFirstPhone + 1}-{Math.min(indexOfLastPhone, filteredPhones.length)} of {filteredPhones.length} phones
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentPhones.map(phone => (<PhoneCard key={phone._id} phone={phone} />))}
          </div>
          
          {currentPhones.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No phones found matching your criteria</p>
            </div>
          )}
          
          <PaginationControls />
        </div>
      </>
    )}
  </>
)}
        {activeTab === 'compare' && (<div className="bg-white rounded-lg shadow-md p-6"><h2 className="text-2xl font-bold mb-6">Compare Phones</h2>{/* eslint-disable-next-line react/jsx-no-undef */}<ComparisonView /></div>)}
        {activeTab === 'rankings' && (<div className="bg-white rounded-lg shadow-md p-6"><h2 className="text-2xl font-bold mb-6">Phone Rankings</h2><CategoryRankings /></div>)}
        {activeTab === 'favorites' && user && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6">My Favorites</h2>
            {favorites.length === 0 ? (<div className="text-center py-16"><Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" /><p className="text-gray-500">No favorites yet</p></div>) : (<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{favorites.map(phone => (<PhoneCard key={phone._id} phone={phone} />))}</div>)}
          </div>
        )}
        {activeTab === 'saved' && user && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6">Saved Comparisons</h2>
            {savedComparisons.length === 0 ? (<div className="text-center py-16"><Save className="w-16 h-16 mx-auto text-gray-300 mb-4" /><p className="text-gray-500">No saved comparisons yet</p></div>) : (
              <div className="space-y-4">
                {savedComparisons.map(comparison => (
                  <div key={comparison._id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                    <div>
                      <h3 className="font-bold text-lg">{comparison.name}</h3>
                      <p className="text-gray-500 text-sm">{comparison.phones.length} phones • {new Date(comparison.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => loadComparison(comparison)} className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">Load</button>
                      <button onClick={() => deleteComparison(comparison._id)} className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
      {!isMobile && (
  <footer className="bg-gray-800 text-white py-8 mt-16">
    <div className="max-w-7xl mx-auto px-4 text-center">
      <p className="text-gray-400">© 2025 PhoneScope. Compare phones globally.</p>
      <p className="text-gray-500 text-sm mt-2">Total phones in database: {phones.length}</p>
    </div>
  </footer>
)}
    </div>
  );
};
export default PhoneScope;